import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { HERO } from '../constants/testIds';

const LINES = ['BULK USED TIRES', 'SHIPPED WORLDWIDE'];
const BADGES = ['Grade A (7mm+) Available', '100% Air Pressure Tested', 'Worldwide Freight Network', '10+ Years in the Tire Business'];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '28%']);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section id="top" ref={ref} data-testid={HERO.section} className="relative min-h-screen flex items-end overflow-hidden bg-brand-bg">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -top-16 h-[120%]">
        <img
          src="/images/brand/truck-loading-dock.jpg"
          alt="BulkUsedTires.com branded truck being loaded with wholesale used tires at our warehouse"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/70 to-brand-bg/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-bg/80 via-transparent to-brand-bg/40" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28 pt-40 w-full">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-testid={HERO.statusPill}
          className="inline-flex items-center gap-2 font-mono text-[11px] sm:text-xs uppercase tracking-widest text-brand-amber bg-brand-surface/80 border border-brand-border rounded-full px-4 py-1.5 mb-7"
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          50,000+ Tires In Stock Through Our Partner Network
        </motion.div>

        <h1 className="font-display font-black uppercase leading-[0.92] tracking-tight text-brand-text text-5xl sm:text-7xl lg:text-8xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: '110%' }}
                animate={{ y: '0%' }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-brand-muted leading-relaxed"
        >
          Wholesale container orders in 800, 1600 &amp; 2400 tire packages — exported to buyers across
          Africa, Latin America, the Middle East, Asia-Pacific and the USA. Laser tread-depth graded,
          pressure tested, and tripled for maximum freight efficiency.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href="#order-form"
            data-testid={HERO.ctaPrimary}
            className="group inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3.5 transition-colors"
          >
            Build Your Bulk Order
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href="#quality-tiers"
            data-testid={HERO.ctaSecondary}
            className="inline-flex items-center gap-2 border border-brand-border text-brand-text font-display font-bold uppercase tracking-wide rounded-full px-7 py-3.5 hover:border-brand-amber transition-colors"
          >
            View Quality Tiers
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-10 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[11px] sm:text-xs uppercase tracking-wider text-brand-muted"
        >
          {BADGES.map((b) => (
            <span key={b} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-green" />
              {b}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
